import VanillaContextMenu from "vanilla-context-menu";

export default class ScrollableContextMenu extends VanillaContextMenu {
  #lastMenuPosition = { x: 0, y: 0 };
  #scrollHandler = null;
  #scrollContainer = null;
  #initialScrollPosition = { x: 0, y: 0 };
  #clickedElement = null;
  #requestAnimationFrameId = null;
  #menuOffset = { x: 0, y: 0 };

  constructor(options) {
    super(options);

    this.#scrollContainer = document.querySelector("#canvas");
    if (!this.#scrollContainer) {
      console.error("Scroll container #canvas not found");
      return;
    }

    const originalHandler = this.options.scope.oncontextmenu;
    this.options.scope.oncontextmenu = (event) => {
      // Store the clicked element
      this.#clickedElement = event.target;

      // Get the element's position
      const elementRect = this.#clickedElement.getBoundingClientRect();

      // Store the initial position relative to the container
      const containerRect = this.#scrollContainer.getBoundingClientRect();
      this.#lastMenuPosition = {
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
      };

      // Store the offset between click position and element position
      this.#menuOffset = {
        x: event.clientX - elementRect.left,
        y: event.clientY - elementRect.top,
      };

      // Store initial scroll position
      this.#initialScrollPosition = {
        x: this.#scrollContainer.scrollLeft,
        y: this.#scrollContainer.scrollTop,
      };

      // Call original handler
      originalHandler(event);

      // Setup scroll handling after menu is shown
      this.#setupScrollHandler();
    };
  }

  #updateMenuPosition = () => {
    const contextMenu = document.querySelector(
      ".cm-bootstrap:not(.nested-context-menu)"
    );

    if (!contextMenu || !this.#clickedElement) {
      if (this.#requestAnimationFrameId) {
        cancelAnimationFrame(this.#requestAnimationFrameId);
      }
      return;
    }

    // Get the clicked element's current position
    const elementRect = this.#clickedElement.getBoundingClientRect();

    // Get contextMenu dimensions
    const menuHeight = contextMenu.offsetHeight;
    const menuWidth = contextMenu.offsetWidth;

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate initial position based on element position and original offset
    let x = elementRect.left + this.#menuOffset.x;
    let y = elementRect.top + this.#menuOffset.y;

    // Check if menu would extend beyond bottom of screen
    if (y + menuHeight > viewportHeight) {
      // Position menu above the cursor instead of below
      y = y - menuHeight;
    }

    // Check if menu would extend beyond right edge of screen
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth;
    }

    // Ensure menu doesn't go off left edge
    if (x < 0) {
      x = 0;
    }

    // Ensure menu doesn't go above top of screen
    if (y < 0) {
      y = 0;
    }

    // Apply position
    contextMenu.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    contextMenu.style.position = "fixed";
    contextMenu.style.left = "0";
    contextMenu.style.top = "0";

    // Position any nested menus
    this.#positionNestedMenus();

    // Request next frame
    this.#requestAnimationFrameId = requestAnimationFrame(
      this.#updateMenuPosition
    );
  };

  #positionNestedMenus() {
    // Get all nested menus
    const nestedMenus = document.querySelectorAll(
      ".cm-bootstrap.nested-context-menu"
    );
    if (!nestedMenus.length) return;

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    nestedMenus.forEach((nestedMenu) => {
      // Find the parent menu (the main context menu)
      const parentMenu = document.querySelector(
        ".cm-bootstrap:not(.nested-context-menu)"
      );
      if (!parentMenu) return;

      // Get parent menu position and dimensions
      const parentRect = parentMenu.getBoundingClientRect();

      // Find the active parent menu item
      const parentItems = document.querySelectorAll(".cm-item.has-nested");
      let activeParentItem = null;

      // Look for the item that has active or focus state
      for (const item of parentItems) {
        if (
          item.classList.contains("active") ||
          item.matches(":focus") ||
          item.classList.contains("cm-active") ||
          item.getAttribute("aria-expanded") === "true"
        ) {
          activeParentItem = item;
          break;
        }
      }

      // If no active item found, use the default behavior (first item)
      if (!activeParentItem && parentItems.length > 0) {
        activeParentItem = parentItems[0];
      }

      if (activeParentItem) {
        // Get the position of the active parent item
        const activeItemRect = activeParentItem.getBoundingClientRect();
        const nestedHeight = nestedMenu.offsetHeight;

        // For precise positioning, directly access the computed style
        const computedStyle = window.getComputedStyle(parentMenu);
        const parentBorderRight =
          parseInt(computedStyle.borderRightWidth, 10) || 0;

        // Position horizontally exactly at the right edge of parent menu, accounting for border
        let posX = parentRect.left + parentRect.width - parentBorderRight;

        // Position vertically aligned with the active item
        let posY = activeItemRect.top;

        // If submenu would go off right edge of screen
        if (posX + nestedMenu.offsetWidth > viewportWidth) {
          // Position to the left of the parent menu
          const nestedMenuBorderLeft =
            parseInt(window.getComputedStyle(nestedMenu).borderLeftWidth, 10) ||
            0;
          posX =
            parentRect.left - nestedMenu.offsetWidth + nestedMenuBorderLeft;
        }

        // If submenu would go off bottom of screen
        if (posY + nestedHeight > viewportHeight) {
          // Align with bottom of viewport
          posY = viewportHeight - nestedHeight;
        }

        // Ensure no gap with precise positioning
        nestedMenu.style.position = "fixed";
        nestedMenu.style.left = "0";
        nestedMenu.style.top = "0";
        nestedMenu.style.transform = `translate3d(${posX}px, ${posY}px, 0)`;

        // Ensure no border on connecting edges to prevent any visual gap
        if (posX > parentRect.left) {
          // Right-positioned: remove left border from nested menu
          nestedMenu.style.borderLeft = "none";
        } else {
          // Left-positioned: remove right border from nested menu
          nestedMenu.style.borderRight = "none";
        }
      }
    });
  }

  #setupScrollHandler() {
    // Clean up any existing handler
    if (this.#scrollHandler) {
      this.#scrollContainer.removeEventListener("scroll", this.#scrollHandler);
      if (this.#requestAnimationFrameId) {
        cancelAnimationFrame(this.#requestAnimationFrameId);
      }
    }

    // Start the animation frame loop
    this.#requestAnimationFrameId = requestAnimationFrame(
      this.#updateMenuPosition
    );

    // Setup scroll handler just to detect scroll events
    this.#scrollHandler = () => {
      // The actual position update happens in the animation frame
    };

    this.#scrollContainer.addEventListener("scroll", this.#scrollHandler, {
      passive: true,
    });
  }

  off() {
    super.off();
    if (this.#scrollHandler) {
      this.#scrollContainer.removeEventListener("scroll", this.#scrollHandler);
      this.#scrollHandler = null;
    }
    if (this.#requestAnimationFrameId) {
      cancelAnimationFrame(this.#requestAnimationFrameId);
      this.#requestAnimationFrameId = null;
    }
    this.#clickedElement = null;
  }
}
